import React, { useRef, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import './globalInventory.css';

const AddBarcodeModal = ({ item, userID, baseUrl, config, onClose }) => {
  const inputRef = useRef(null);
  const [barcode, setBarcode] = useState(item?.barcode || '');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setBarcode(item?.barcode || '');
    inputRef.current?.focus();
  }, [item]);

  const callAddBarcode = async () => {
    if (!barcode) {
      alert('Please scan or enter barcode');
      return { ok: false, message: 'No barcode' };
    }

    try {
      const res = await fetch(`${baseUrl}/api/Inventory/v1.0/addBarcodeToItem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: config?.headers?.Authorization,
        },
        body: JSON.stringify({
          userid: userID,
          inventoryId: item.id,
          barCode: barcode,
        }),
      });

      const result = await res.json();
      if (result?.responseCode === '00') {
        return { ok: true, message: result?.responseMessage || 'Barcode added successfully' };
      }
      return { ok: false, message: result?.responseMessage || 'Error adding barcode' };
    } catch (err) {
      console.error(err);
      return { ok: false, message: 'Network error' };
    }
  };

  const callAddToMainInventory = async () => {
    // Assuming API expects an array of items; sending current item only.
    // If your API expects a different shape, adjust the payload accordingly.
    try {
      const res = await fetch(`${baseUrl}/api/Inventory/v1.0/addProductToMainInventory`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: config?.headers?.Authorization,
        },
        body: JSON.stringify({
          userid: userID,
          // common patterns are either [id] or [{ inventoryId: id }]; using simple [id]
          items: [item.id],
        }),
      });

      const result = await res.json();
      if (result?.responseCode === '00') {
        return { ok: true, message: result?.responseMessage || 'Item added to main inventory' };
      }
      return { ok: false, message: result?.responseMessage || 'Failed to add item to main inventory' };
    } catch (err) {
      console.error(err);
      return { ok: false, message: 'Server error. Please try again.' };
    }
  };

  // 1) Only add/update barcode
  const handleSubmit = async () => {
    setLoading(true);
    const a = await callAddBarcode();
    setLoading(false);

    alert(a.message);
    if (a.ok) onClose?.();
  };

  // 2) Add barcode, then add product to main inventory (only if barcode succeeded)
  const handleSubmitAndAdd = async () => {
    setLoading(true);
    const a = await callAddBarcode();
    if (!a.ok) {
      setLoading(false);
      alert(a.message);
      return;
    }

    const b = await callAddToMainInventory();
    setLoading(false);

    // Show both outcomes nicely
    if (b.ok) {
      alert(`✅ ${a.message}\n✅ ${b.message}`);
      onClose?.();
    } else {
      alert(`✅ ${a.message}\n❌ ${b.message}`);
      // keep modal open so user can retry adding to main inventory if needed
    }
  };

  return (
    <div className="gi-modal-overlay">
      <div className="gi-barcode-modal">
        <div className="gi-modal-header">
          <h3>Add Barcode to {item?.productName}</h3>
          <button className="gi-modal-close" onClick={onClose} disabled={loading}>
            <X size={20} />
          </button>
        </div>

        {item?.productImage ? (
          <img src={item.productImage} alt={item?.productName || 'Product'} className="gi-modal-image" />
        ) : null}

        {(item?.category || item?.subCategory) && (
          <p className="gi-modal-details">{[item?.category, item?.subCategory].filter(Boolean).join(' → ')}</p>
        )}

        {Boolean(item?.barcode) && (
          <div
            className="gi-assigned-chip"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 10px',
              borderRadius: 8,
              background: '#F4F6F8',
              fontSize: 12,
              margin: '8px 0',
            }}
          >
            <span style={{ opacity: 0.7 }}>Assigned barcode:</span>
            <code style={{ fontWeight: 600 }}>{item.barcode}</code>
          </div>
        )}

        <input
          ref={inputRef}
          type="text"
          placeholder="Add or update barcode…"
          value={barcode}
          onChange={(e) => setBarcode(e.target.value)}
          className="gi-barcode-input"
          disabled={loading}
        />

        <div className="d-flex gap-2 g-2">
          <button className="gi-btn-submit" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Please wait…' : 'Submit'}
          </button>
          <button className="gi-btn-submit-add" onClick={handleSubmitAndAdd} disabled={loading}>
            {loading ? 'Processing…' : 'Submit & Add'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddBarcodeModal;
